<script lang="ts">
    import { Component, mixins, Prop } from "vue-facing-decorator";
    import { Promotion } from "webshop-api";
    import { IconName, TranslationKey, Url, PerformanceQASelectors } from "webshop-modules";
    import { panelStateService, useBestSeatStore, usePromotionStore } from "@/domain/ticket-selection";
    import { BreakpointHandler } from "@/mixins";
    import { ButtonType } from "../../atoms/button/button-type.enum";
    import SpinnerComponent from "@/components/atoms/Spinner.vue";
    import SelectionComponent from "@/components/molecules/Selection.vue";
    import TextInputComponent from "@/components/atoms/form/TextInput.vue";
    import ButtonComponent from "@/components/atoms/button/Button.vue";
    import VueMultiselect from "vue-multiselect/src/Multiselect.vue";
    import IconComponent from "@/components/atoms/Icon.vue";

    @Component({
        components: {
            IconComponent,
            ButtonComponent,
            TextInputComponent,
            SelectionComponent,
            SpinnerComponent,
            VueMultiselect,
        }
    })
    export default class PromotionPanel extends mixins(BreakpointHandler) {

        @Prop() public eventId!: number;

        public readonly Translation: typeof TranslationKey = TranslationKey;
        public readonly ButtonType: typeof ButtonType = ButtonType;
        public readonly IconName: typeof IconName = IconName;
        public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
        public readonly promotionStore = usePromotionStore();
        public readonly panelState = panelStateService;

        public selectionPlaceholder = 'Select promotion campaign';
        public applyButtonLabel = 'Apply promotion';
        public inputPlaceholder = 'Enter promotion code';
        public isPromoSelectionOpen = false;

        private readonly bestSeatStore = useBestSeatStore();

        private inputValue = '';

        public selectDropdownPromotion(promotion: Promotion): void {
            this.handleSelection(promotion);
            this.togglePromoSelection();
        }

        public handleRemoval(): void {
            this.handleInput('');
            this.promotionStore.removePromotion();
            this.bestSeatStore.activatePromotion(0);
            this.selectDefaultPromotion();
        }

        public togglePromoSelection(): void {
            this.isPromoSelectionOpen = !this.isPromoSelectionOpen;
        }


        public selectionText(): string {
            return (this.promotionStore.selection && this.promotionStore.selection.name) ?? this.selectionPlaceholder;
        }

        public shouldDisplayBottomBorder(): boolean {
            return !this.isMobile &&
                (this.panelState.isPromotionPanelActive() || this.promotionStore.active !== null);
        }

        public get canSubmit(): boolean {
            if (this.isCodeRequired) {
                return !!this.selection && this.inputValue !== '';
            }

            return !!this.selection;
        }

        public get activePromotion() {
            return this.promotionStore.active;
        }

        public get isCodeRequired(): boolean {
            if (!this.selection) {
                return false;
            }

            return this.promotionStore.selection?.isCodeRequired ?? false;
        }

        public get selection(): Promotion | null {
            if (!this.promotionStore.selection) {
                return null;
            }

            return this.promotionStore.selection;
        }

        public get activePromotionLabel() {
            return this.isMobile ? `${this.promotionStore.active!.name} : ${this.promotionStore.code}` : this.promotionStore.code;
        }

        public handleInput(value: string): void {
            this.inputValue = value;
        }

        public applyNonValidatingPromotion(): void {
            this.promotionStore.activatePromotion(this.$translationService);
            this.activatePromotionOnBestSeat();
        }

        public async handleSubmit(event: Event): Promise<void> {
            if (!this.canSubmit) {
                return;
            }

            if (!this.isCodeRequired) {
                this.applyNonValidatingPromotion();

                return;
            }

            await this.promotionStore.validatePromotion(
                {
                    code: this.inputValue,
                    eventId: this.eventId
                },
                this.$translationService,
                this.$apiService
            );

            this.activatePromotionOnBestSeat();

            if (!this.promotionStore.error) {
                this.panelState.closePanel();
            }
        }

        private async created() {
            this.selectDefaultPromotion();

            this.selectionPlaceholder = await this.$translationService.getTranslationFor(
                this.Translation.Promotion_select_campaign
            );

            this.inputPlaceholder = await this.$translationService.getTranslationFor(
                this.Translation.Promotion_enter_code_placeholder
            );

            this.applyButtonLabel = await this.$translationService.getTranslationFor(
                this.Translation.Promotion_apply_promotion
            );

            await this.validatePromotionParams();
        }

        private async validatePromotionParams() {
            const parsedUrl = new Url(window.location.href);
            const id = parsedUrl.getSearchParam('promotionId');
            const code = parsedUrl.getSearchParam('promotionCode');
            let promotion: Promotion | undefined;

            if (!id) {
                return;
            }

            promotion = this.promotionStore.availablePromotions.find(item => item.id === Number.parseInt(id));
            if (!promotion) {
                return;
            }

            await this.promotionStore.selectPromotion(promotion);

            if (!promotion.isCodeRequired) {
                await this.promotionStore.activatePromotion(this.$translationService);
                this.activatePromotionOnBestSeat();

                return;
            }

            if (promotion.isCodeRequired && code) {
                this.handleInput(code);
                await this.promotionStore.validatePromotion(
                    {
                        code,
                        eventId: this.eventId
                    },
                    this.$translationService,
                    this.$apiService,
                );

                this.activatePromotionOnBestSeat();
            }
        }

        private activatePromotionOnBestSeat() {
            if (this.promotionStore.active && this.promotionStore.active.id) {
                this.bestSeatStore.activatePromotion(this.promotionStore.active.id);
            }
        }

        private handleSelection(promotion: Promotion): void {
            this.promotionStore.selectPromotion(promotion);
        }

        private selectDefaultPromotion(): void {
            if (this.promotionStore.availablePromotions.length === 1) {
                this.handleSelection(this.promotionStore.availablePromotions[0]);
            }
        }

    }
</script>


<template>
    <div v-if="promotionStore.availablePromotions.length > 0"
         id="event-promotion-panel"
         v-bind:class="{
            '-no-border-top': !isMobile,
            '-no-border-bottom': !shouldDisplayBottomBorder() ,
            '-no-padding': !panelState.isPromotionPanelActive() && !promotionStore.active
         }"
         class="o-eventPromotion">
        <div v-if="promotionStore.canDisplayHeader"
             v-bind:class="{ '-fullWidth': !isMobile }"
             class="o-eventPromotion__heading">
            <template v-if="activePromotion">
                <h3 v-translate="{
                    key: Translation.Promotion_selected_promotion_interface_title,
                    keyValues: JSON.stringify({ promotionName: activePromotion.name })
                }">Promotion: {{ activePromotion.name }}</h3>
            </template>
        </div>

        <div v-bind:class="{ '-has-padding': !isMobile && activePromotion }"
            class="o-eventPromotion__form -in-column">
            <div v-if="promotionStore.spinner" class="o-eventPromotion__spinner">
                <SpinnerComponent></SpinnerComponent>
            </div>

            <template v-if="promotionStore.active !== null">
                <SelectionComponent :value="activePromotionLabel" v-on:remove="handleRemoval"/>
            </template>

            <template v-else>
                <div v-if="panelState.isPromotionPanelActive()" class="o-eventPromotion__selectionContainer">
                    <div v-on:click="togglePromoSelection()"
                         v-bind:class="{'-has-border' : isPromoSelectionOpen}"
                         class="o-eventPromotion__promotionSelection">
                        <p v-bind:class="{ '-has-opacity': !promotionStore.selection }"
                           class="o-eventPromotion__promotionName"> {{ selectionText() }} </p>
                        <div class="o-eventPromotion__promotionDropdown">
                            <IconComponent :icon-id="IconName.Arrow"
                                v-bind:class="isPromoSelectionOpen ?
                                    'o-eventPromotion__collapseArrow'
                                    : 'o-eventPromotion__expandArrow'"></IconComponent>
                        </div>
                    </div>
                    <transition-group name="slide">
                        <div v-show="isPromoSelectionOpen"
                             v-for="(promotion, index) in promotionStore.availablePromotions"
                             v-bind:class="{ '-active': promotion.id === promotionStore.selection?.id }" v-bind:key="index"
                             class="o-eventPromotion__promotionSelection"
                             v-on:click="selectDropdownPromotion(promotion)">
                            <p> {{ promotion.name }} </p>
                        </div>
                    </transition-group>
                </div>

                <div v-if="selection && panelState.isPromotionPanelActive()"
                     v-on:keyup.enter="handleSubmit"
                     v-bind:class="{ 'is-button-group' : !selection.isCodeRequired }">
                    <TextInputComponent v-if="selection.isCodeRequired"
                                        :label="inputPlaceholder"
                                        :name="'promotion-code'"
                                        :value="inputValue"
                                        :hasSubmitAction="false"
                                        :input-qa-selector="PerformanceQASelectors.PROMOTION_CODE"
                                        :action-qa-selector="PerformanceQASelectors.PROMO_ARROW"
                                        v-on:input="handleInput">
                    </TextInputComponent>
                </div>

                <button-component v-if="panelState.isPromotionPanelActive()"
                    :label="applyButtonLabel"
                    :active="canSubmit"
                    v-on:submit="handleSubmit"/>
            </template>

            <p v-if="!activePromotion && panelState.isPromotionPanelActive()"
               v-translate="{ key : Translation.Promotion_interface_subtitle }"
               class="o-eventPromotion__pitch">Select promotion and enter your promotion code.</p>

            <div v-if="promotionStore.error && panelState.isPromotionPanelActive()" class="o-eventPromotion__footer">
                <p class="a-notice__error">{{ promotionStore.error }}</p>
            </div>
        </div>
    </div>
</template>